import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    TCITenantSelectorType,
    ITCIHybridAgentList,
    ITCIHybridAgentStatusType,
    TCIAgentStatusCategory,
    USER_ROLES,
    ITCIHybridAgentApps
} from "@tci/common";
import { BaseAppDetailMenuComponentOutput } from "../../ng-app-details/ng-app-details-menu";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { ITCICEnvToolsComponentOutput } from "../../ng-tenant/tci-tenant-components";
import { BaseAgentListService } from "./base-agent-list.service";
import { NgEnvToolsComponentInput } from "../tool-loader/ng-env-tools-loader.component";
import { BehaviorSubject, Subscription } from "rxjs";
import { NgPaginatorComponent } from "../../ng-shared/ng-components/ng-paginator/ng-paginator.component";
import * as lodash from "lodash";
import { NgWaitService } from "../../ng-shared/ng-components/ng-wait/ng-wait.service";
import { NgUserService } from "../../ng-shared/ng-services/ng-user.service";
import { Dialog } from "primeng/primeng";
/**
 * This component is the base component for All tenant who will use the
 * hybrid agent. Those tenants needs to extend this class only and provide
 * no functionality as it is handled here. For Scribe tenant, the current
 * Component implementing the TCITenantSelectorType.TCI_ENV_TOOLS_AGENTS
 * selector will change the selector to TCITenantSelectorType.TCI_ENV_TOOLS_AGENTS
 * and will only provide the list. The Create button button functionality
 * is moving to a popup loaded by the base component BaseAgentComponent using selector
 * TCITenantSelectorType.TCI_ENV_TOOLS_AGENTS. All tenants will need to
 * extend the BaseAgentComponent and provide no functionality as it will be
 * handled in the base. All the three create options identified by selectors
 *      public static TCI_ENV_TOOLS_AGENT_CONNECT_CLOUD = new TCITenantSelectorType("tci-env-tools-agent-conn-cloud");
 *      public static TCI_ENV_TOOLS_AGENT_CONNECT_ON_PREM = new TCITenantSelectorType("tci-env-tools-agent-conn-on-prem");
 *      public static TCI_ENV_TOOLS_AGENT_HYBRID = new TCITenantSelectorType("tci-env-tools-agent-hybrid");
 *      public static TCI_ENV_TOOLS_AGENT_UNIFIED_MGMT = new TCITenantSelectorType("tci-env-tools-agent-unified-mgmt");
 * are dynamic components to be loaded by the NgBaseAgentMenuModalComponent(base-agent-menu-modal.component)
 * which provides the popup functionality where the tenant component will be loaded inside the popup to do its work.
 */

const AGENT_SORTABLE_ROWS = {
    NAME: "Name",
    STATUS: "Status",
    AGENT_HOST: "Agent Host",
    SERVICE: "Service",
    VERSION: "Version",
    LAST_STARTUP: "Last Startup",
    LAST_CONTACT: "Last Contact"
};

const AGENT_COLUMN_RESPONSE_KEY_MAP = {
    "Name": "name",
    "Status": "status",
    "Agent Host": "machine",
    "Service": "id",
    "Version": "version",
    "Last Startup": "lastStartup",
    "Last Contact": "lastContact"
};

const AGENT_STATUS_INFO = "statusInfo";
const AGENT_APPS_INFO = "appsInfo";
const TOOLTIP_INFO = "tooltip";
const TOOLTIP_HR = "\n-------------------------\n";

@Component({
    selector: TCITenantSelectorType.TCI_ENV_TOOLS_AGENTS_LIST.toString(),
    templateUrl: "./base-agent-list.component.html",
    styleUrls: ["./base-agent-list.component.less"]
})
export class BaseEnvToolsHybridAgentListComponent<E, I extends NgEnvToolsComponentInput,
    O extends BaseAppDetailMenuComponentOutput<ITCICEnvToolsComponentOutput<any>>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit, OnDestroy {

    @ViewChild("pager") pager: NgPaginatorComponent;
    @ViewChild("dialogDeleteAgent") dialogDeleteAgent: Dialog;

    private deleteAgentModalShow = false;
    private deleteAgentInfo: ITCIHybridAgentList;
    private agents: ITCIHybridAgentList[] = [];
    private agentApps: ITCIHybridAgentApps[] = [];
    private subscriptions: Subscription[] = [];
    private isAdminUser = false;
    // Sorting
    private SORTABLE_ROWS = AGENT_SORTABLE_ROWS;
    private sortedBy: string;
    private sortAsc = false;

    // Pagination
    private pageStart = 0;
    private pages = 10;
    private paginatedRows$: BehaviorSubject<ITCIHybridAgentList[]> = new BehaviorSubject([]);
    private pageRestoreValue: number;

    constructor(
        private agentListService: BaseAgentListService,
        private translate: TranslateService,
        private toastr: NgToastrService,
        private blockUiSvc: NgWaitService,
        private userService: NgUserService) {
        super();
    }

    private loadingSource: boolean;

    ngOnInit(): void {
        this.blockUiSvc.block();
        this.subscriptions.push(
            this.agentListService.getAgentList()
            .finally(() => this.blockUiSvc.unblock())
            .subscribe((agents: ITCIHybridAgentList[]) => {
                this.agents = agents;
                this.defaultSort(this.agents);
                this.agentListService.getAgentApps()
                .finally(() => {
                    this.setInitialStatus();
                    this.setPaginatedRows();
                })
                .subscribe(
                    (appsInfo: ITCIHybridAgentApps[]) => this.agentApps = appsInfo
                );
            }, err => {
                console.log(`Unable to load hybrid agent list: ${err}`);
            }),
            this.userService.getUserInfo().subscribe(token => {
                this.isAdminUser = token.rolX[0] === USER_ROLES.ADM;
            })
        );
    }

    setInitialStatus() {
      this.agents.forEach(agent => {
          if (agent.status === ITCIHybridAgentStatusType.healthy) {
              agent[AGENT_STATUS_INFO] = TCIAgentStatusCategory.HEALTHY;
          } else if (agent.status === ITCIHybridAgentStatusType.heartbeatLost) {
              agent[AGENT_STATUS_INFO] = TCIAgentStatusCategory.HEARTBEATLOST;
          }
          const APPS = this.agentApps.filter(infoObj => infoObj.agentId === agent.id);
          if (APPS.length > 0) {
              agent[AGENT_APPS_INFO] = APPS[0].apps;
              agent[TOOLTIP_INFO] = `<p><b>Monitored Remote Apps:</b></p>` + this.formatArrayToString(APPS[0].apps) + `<br />`;
          } else {
              agent[AGENT_APPS_INFO] = [];
              agent[TOOLTIP_INFO] = "";
          }
      });
      // Map Tunnel AccessKey & Specs -> TOOLTIP_INFO for the corresponding agents
      this.getAgentTunnelSpecs();
    }

    private formatArrayToString(data: string[]): string {
        let result = "";
        data.forEach((s, i) => {
            result += s + `${(i === data.length - 1) ? "<br />" : ",<br />"}`;
        });
        return result;
    }
     // <Paginator instructions>
     private setPaginatedRows() {
        const part = this.agents.slice(this.pageStart, this.pageStart + this.pages);
        if (part) {
            if (part.length === 0 && this.pager) {
                const prevPage = this.pager.getPage() - 1;
                if (prevPage > 0) {
                    this.pager.changePage(prevPage);
                } else {
                    this.paginatedRows$.next(part); // refresh list when delete last entry
                }
            } else {
                this.paginatedRows$.next(part);
            }
        }
    }

    /**
     * Fetch the Tunnel Specs of the agents having an access key.
     * Append it to the TOOLTIP_INFO of the corresponding agent.
     */
    private getAgentTunnelSpecs() {
        const agentsWithAccKeys = this.agents.filter(a => a.tunnelAccessKey && a.tunnelAccessKey !== "");
        const agentIds = agentsWithAccKeys.map(a => a.id);
        if (agentIds.length) {
            this.subscriptions.push(
                this.agentListService.getAgentTunnelSpecs(agentIds)
                .subscribe(tunnelSpecs => {
                    agentsWithAccKeys.map(agent => {
                        agent[TOOLTIP_INFO] += `<br /><p><b>TCI Apps connected resources on access key:</b></p> ${agent.tunnelAccessKey}<br />`;
                        const curr = tunnelSpecs.find(spec => spec.id === agent.id);
                        agent[TOOLTIP_INFO] += curr.specs.length ? "<br /><p><b>Specs:</b></p>" + this.formatArrayToString(curr.specs) : "";
                    });
                    const fetchFailedAgents = tunnelSpecs.filter(spec => spec.error);
                    if (fetchFailedAgents.length) {
                        // Error: fetching Tunnel Specs failed
                        console.log("Failed Fetching Tunnel Status for the agents: ", fetchFailedAgents.map(info => info.id));
                    }
                })
            );
        }
    }

    private onPageChange($event: number) {
        this.pageStart = $event;
        this.setPaginatedRows();
    }

    private defaultSort(agents) {
        this.agents = agents.sort((a, b) => this.sortByName(a, b));
    }

    private toggleSort(sortBy: string) {
        this.sortAsc = !this.sortAsc;
        this.sortedBy = sortBy;
        if (sortBy === this.SORTABLE_ROWS.NAME) {
            this.agents = this.agents.sort((a, b) => this.sortByName(a, b)).reverse();
        } else {
            this.agents = lodash.sortBy(this.agents, AGENT_COLUMN_RESPONSE_KEY_MAP[sortBy],
                this.agents.sort((a, b) => this.sortByName(a, b)).reverse()).reverse();
        }
        if (this.sortAsc) {
            this.agents.reverse();
        }
        this.setPaginatedRows();
    }

    private sortByName(a, b) {
        return Intl.Collator(undefined, {
            numeric: true,
            sensitivity: "base"
        }).compare(a.name, b.name);
    }

    deleteAgent() {
        const agentId = this.deleteAgentInfo.id;
        this.blockUiSvc.block();
        this.subscriptions.push(
            this.agentListService.deleteAgent(agentId)
            .finally(() => {
                this.blockUiSvc.unblock();
                this.deleteAgentInfo = undefined;
                this.deleteAgentModalShow = false;
            })
            .subscribe(res => {
                this.agents.splice(this.agents.findIndex(agent => agent.id === agentId), 1);
                this.setPaginatedRows();
                this.toastr.success("Agent deleted successfully");
            }, err => {
                this.toastr.error(err.errorMsg);
            })
        );
    }

    modalCenterDeleteAgent() {
        this.dialogDeleteAgent.center();
    }

    private deleteAgentConfirm(agent: ITCIHybridAgentList) {
        this.deleteAgentInfo = agent;
        this.deleteAgentModalShow = true;
        this.toastr.clear();
    }

    private deleteCancel() {
        this.deleteAgentInfo = undefined;
        this.deleteAgentModalShow = false;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
